import type { ArchiveFieldKind, ArchiveImport, ArchiveType, FileAnalysis, SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiUpload, errorMessage } from '../../lib/api';
import { humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, ErrorText, Field, Select } from '../../ui';
import { SourceProgress, UploadState, useQueuedUpload, useWork } from './WorkProgress';

const slug = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'c_$1') || 'campo';

/** Prima volta di un layout: come leggere le colonne, in quale tipo, con quale chiave. */
function MappingForm({ analysis, onDone }: { analysis: FileAnalysis; onDone: (r: ArchiveImport) => void }) {
  const { t, isManager } = useTenant();
  const columns = analysis.colonne ?? [];
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const [typeName, setTypeName] = useState('');
  const [typeLabel, setTypeLabel] = useState('');
  const [rows, setRows] = useState(() => columns.map((c) => ({ colonna: c.nome, includi: true, attributo: slug(c.nome), tipo: c.tipo as ArchiveFieldKind })));
  const [key, setKey] = useState<string | null>(null);
  const confirm = useMutation({
    mutationFn: () =>
      api<ArchiveImport>(t(`/archive/documents/${analysis.documento_id}/mapping`), {
        body: {
          tipo: { nome: typeName, etichetta: typeLabel || humanize(typeName) },
          attributi: rows.filter((r) => r.includi).map(({ colonna, attributo, tipo }) => ({ colonna, attributo, tipo })),
          colonna_chiave: key,
        },
      }),
    onSuccess: onDone,
  });
  if (!isManager) return <p className="text-sm text-ink/60">Un responsabile deve confermare come leggere questo tipo di file.</p>;
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink/70">È la prima volta che arriva un file con queste colonne. Indica come leggerlo: dalla prossima volta lo stesso tipo di export si importa da solo.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Field
            label="Cosa contiene ogni riga (tipo)"
            list="tipi-esistenti"
            placeholder="es. camera, fattura, dipendente"
            value={typeName}
            onChange={(e) => {
              setTypeName(slug(e.target.value));
              setTypeLabel(types.data?.data.find((x) => x.nome === slug(e.target.value))?.etichetta ?? '');
            }}
          />
          <datalist id="tipi-esistenti">
            {types.data?.data.map((x) => (
              <option key={x.nome} value={x.nome}>
                {x.etichetta}
              </option>
            ))}
          </datalist>
        </div>
        <Field label="Nome leggibile" placeholder="es. Camera" value={typeLabel} onChange={(e) => setTypeLabel(e.target.value)} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-sm">
          <thead>
            <tr className="text-left text-xs text-ink/55">
              <th className="pb-1">Importa</th>
              <th className="pb-1">Colonna del file</th>
              <th className="pb-1">Diventa</th>
              <th className="pb-1">Tipo</th>
              <th className="pb-1" title="Identifica la riga: con la chiave, un export successivo aggiorna invece di duplicare">
                Chiave
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.colonna} className={r.includi ? '' : 'opacity-50'}>
                <td className="py-1">
                  <input type="checkbox" checked={r.includi} onChange={(e) => setRows(rows.map((x, n) => (n === i ? { ...x, includi: e.target.checked } : x)))} />
                </td>
                <td className="py-1 pr-2">{r.colonna}</td>
                <td className="py-1 pr-2">
                  <input className="w-full rounded border border-black/15 px-2 py-1" value={r.attributo} onChange={(e) => setRows(rows.map((x, n) => (n === i ? { ...x, attributo: slug(e.target.value) } : x)))} />
                </td>
                <td className="py-1 pr-2">
                  <Select value={r.tipo} onChange={(e) => setRows(rows.map((x, n) => (n === i ? { ...x, tipo: e.target.value as ArchiveFieldKind } : x)))}>
                    <option value="testo">testo</option>
                    <option value="numero">numero</option>
                    <option value="data">data</option>
                  </Select>
                </td>
                <td className="py-1 text-center">
                  <input type="radio" name="chiave" checked={key === r.colonna} disabled={!r.includi} onChange={() => setKey(r.colonna)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={!typeName || confirm.isPending || !rows.some((r) => r.includi)} onClick={() => confirm.mutate()}>
          Conferma e importa {analysis.righe_totali} righe
        </Button>
        {key === null && <span className="text-xs text-amber-700">Senza chiave ogni caricamento aggiunge righe nuove.</span>}
      </div>
      <ErrorText>{confirm.error ? errorMessage(confirm.error) : ''}</ErrorText>
    </div>
  );
}

/**
 * Dopo un import: da quale programma arriva l'export? Serve a capire come lavora l'azienda e, se il
 * programma si può collegare, a proporlo con i numeri (quanti file arrivano a mano).
 */
function SourceProgram({ signatureId }: { signatureId: string }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const software = useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software');
  const [name, setName] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const link = useMutation({
    mutationFn: (n: string) => api<SoftwareDto>(t('/software'), { body: { name: n, how: 'export di file', description: 'esporta file caricati in archivio', signature_id: signatureId } }),
    onSuccess: (_r, n) => {
      setSaved(n);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'software'] });
    },
  });
  const known = software.data?.data.find((s) => s.signature_ids.includes(signatureId));
  if (!isManager || !software.data) return null;
  if (known || saved)
    return (
      <p className="text-xs text-ink/55">
        Arriva da <button className="text-accent hover:underline" onClick={() => navigate(`/t/${tenant.id}/software`)}>{known?.name ?? saved}</button>.
      </p>
    );
  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-lg bg-black/[0.03] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) link.mutate(name.trim());
      }}
    >
      <div className="min-w-[12rem] flex-1">
        <Field label="Da quale programma arriva questo file? (facoltativo)" list="software-noti" placeholder="es. il gestionale, il programma delle presenze…" value={name} onChange={(e) => setName(e.target.value)} />
        <datalist id="software-noti">
          {software.data.data.map((s) => <option key={s.id} value={s.name} />)}
        </datalist>
      </div>
      <Button type="submit" variant="ghost" disabled={!name.trim() || link.isPending}>Indica</Button>
      <p className="w-full text-xs text-ink/45">Così il sistema verifica se quel programma si può collegare, invece di caricare i file a mano.</p>
      <ErrorText>{link.error ? errorMessage(link.error) : ''}</ErrorText>
    </form>
  );
}

/** Un foglio (CSV, Excel) da solo si analizza subito per importarne le righe; tutto il resto va nella coda. */
const TABULAR = /\.(csv|tsv|xlsx|xls|ods)$/i;

/**
 * Caricamento generale dell'archivio (fuori dalle cartelle dei documenti obbligatori): `take` riceve i file (dal
 * pulsante o trascinati), `panel` mostra l'invio, l'anteprima del foglio e la mappatura.
 */
export function useGeneralUpload() {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [analysis, setAnalysis] = useState<FileAnalysis | null>(null);
  const [done, setDone] = useState<ArchiveImport | null>(null);
  const queued = useQueuedUpload('archivio');
  const upload = useMutation({
    mutationFn: async (f: File) => {
      setAnalysis(null);
      return apiUpload<FileAnalysis>(t('/archive/upload'), f);
    },
    onSuccess: (a) => {
      setAnalysis(a);
      setDone(a.importazione ?? null);
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive'] });
    },
  });
  const take = (files: File[]) => {
    // Un foglio da solo: anteprima e importazione delle righe. Documenti, più file, archivi compressi: coda di
    // lavorazione con la barra file per file (ADR 0043).
    if (files.length === 1 && TABULAR.test(files[0]!.name)) return upload.mutate(files[0]!);
    setAnalysis(null);
    void queued.send(files);
  };
  const finish = (r: ArchiveImport) => {
    setDone(r);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'archive'] });
  };
  const busy = upload.isPending || queued.uploading;
  const label = upload.isPending ? 'Lettura del file…' : queued.uploading ? `Invio dei file: ${queued.sent} di ${queued.total}…` : null;
  const panel = (
    <>
      <ErrorText>{upload.error ? errorMessage(upload.error) : ''}</ErrorText>
      <UploadState q={queued} />
      {analysis && (
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {analysis.tipo_file === 'documento' ? (
                <div className="text-sm">
                  <p className="font-medium">Documento registrato · {analysis.pagine} pagine · il sistema lo sta leggendo</p>
                  {analysis.senza_testo ? (
                    <p className="text-amber-700">Non contiene testo leggibile (probabilmente una scansione): la lettura delle scansioni arriverà in seguito.</p>
                  ) : (
                    <p className="mt-1 line-clamp-4 text-ink/60">{analysis.testo}</p>
                  )}
                </div>
              ) : done ? (
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  {analysis.firma?.nota ? 'Tipo di export riconosciuto. ' : ''}Importate in “{humanize(done.tipo)}”: {done.nuove} nuove, {done.aggiornate} aggiornate.{' '}
                  <button className="underline" onClick={() => navigate(`/t/${tenant.id}/dati/salvati/${done.tipo}`)}>
                    Vedi in Dati
                  </button>
                </p>
              ) : null}
            </div>
            <button className="shrink-0 rounded-lg px-2 py-1 text-xs text-ink/50 hover:bg-black/5" onClick={() => setAnalysis(null)}>
              Chiudi
            </button>
          </div>
          {analysis.tipo_file === 'tabella' && done && analysis.firma && <SourceProgram signatureId={analysis.firma.id} />}
          {analysis.tipo_file === 'documento' || done ? null : (
            <>
              <p className="text-sm text-ink/60">
                {analysis.righe_totali} righe{analysis.foglio ? ` dal foglio “${analysis.foglio}”` : ''}. Anteprima:
              </p>
              <div className="max-h-48 overflow-auto rounded border border-black/10">
                <table className="w-full text-xs">
                  <thead className="bg-black/[0.03]">
                    <tr>{analysis.colonne?.map((c) => <th key={c.nome} className="px-2 py-1 text-left font-medium">{c.nome}</th>)}</tr>
                  </thead>
                  <tbody>
                    {analysis.anteprima?.slice(0, 8).map((r, i) => (
                      <tr key={i} className="border-t border-black/5">
                        {analysis.colonne?.map((c) => <td key={c.nome} className="px-2 py-1">{String(r[c.nome] ?? '')}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <MappingForm analysis={analysis} onDone={finish} />
            </>
          )}
        </div>
      )}
    </>
  );
  return { take, busy, label, panel };
}

/** I file in lavorazione (e quelli finiti nelle ultime ore): la barra resta anche dopo aver ricaricato la pagina. */
export function WorkingCard() {
  const work = useWork();
  const sources = (work.data?.sources ?? []).filter((s) => s.kind === 'caricamento' || s.kind === 'documenti');
  if (!sources.length) return null;
  return (
    <Card>
      <h2 className="mb-3 text-sm font-semibold">File in lavorazione</h2>
      <div className="space-y-2">
        {sources.slice(0, 8).map((s) => (
          <SourceProgress key={s.id} source={s} />
        ))}
      </div>
    </Card>
  );
}
